import { Navigate, Route, Routes } from "react-router-dom";

import { HomeRoute } from "@/routes/HomeRoute";
import { AuthGuard } from "@rook/shared/auth/AuthGuard";
import { AuthProvider } from "@rook/shared/auth/AuthProvider";
import { CrossOriginHandoff } from "@rook/shared/components/CrossOriginHandoff";
import { lazyWithReload } from "@rook/shared/lib/lazyWithReload";
import { LANDING_ORIGIN } from "@rook/shared/lib/portalOrigins";


/**
 * `@rook/portal` App — the per-env per-tenant application that
 * customers spend their working day in. Deploys to
 * `<env>.clients.portal.tryrook.io` in cuenta 3 (prod) or cuenta 8
 * (sandbox + dev).
 *
 * Routes owned here (the entire /home/* tree):
 *   /home                     → DashboardView (index)
 *   /home/users               → UsersView
 *   /home/score               → ScoreView
 *   /home/logs                → LogsView
 *   /home/data-sources        → DataSourcesView
 *   /home/extraction/*        → TimezoneView | PreExistingDataView | RetriesView
 *   /home/delivery/*          → WebhooksView | GranularView | NonStructuredView | DataStructureView
 *   /home/develop/*           → SimulatorView | ConnectionPageView
 *   /home/auth/*              → ApiKeysView | SdkKeysView
 *   /home/account             → AccountView
 *   /home/status-page         → StatusPageView
 *
 * What's intentionally NOT here (lives in `@rook/landing`):
 *  - /, /create-account, /forgot-password, /forgot-password/reset
 *  - /legal, /legal/:slug
 *
 * Cross-origin handoffs:
 *  - Anything OUTSIDE /home → render the branded overlay then
 *    `window.location.assign('<landing-origin>/<path>')`. The
 *    landing app owns auth + legal so deep-linking to those URLs
 *    here is invalid — bounce to landing with the same path.
 *  - Unauthenticated user hitting /home/* → `<AuthGuard>` bounces
 *    to `/` which then triggers the back-to-landing overlay (the
 *    landing's own LoginView is the real destination).
 *
 * Why lazy-loaded views: each feature's deps (recharts for the
 * Dashboard, the user-detail Dialog on Users, etc.) only ship when
 * the customer opens that view. Saves ~150 KB on cold-load. We use
 * `lazyWithReload` instead of React's `lazy` directly so a stale-
 * chunk error after a deploy (the OLD `index.html` referencing a
 * chunk hash that no longer exists in S3) triggers a single
 * `location.reload()` instead of crashing the SPA. See
 * `@rook/shared/lib/lazyWithReload` for the full design rationale
 * and the ref `0e6e0671` postmortem that motivated this.
 */

const DashboardView = lazyWithReload(() =>
  import("@/features/dashboard/DashboardView").then((mod) => ({
    default: mod.DashboardView,
  })),
);
const UsersView = lazyWithReload(() =>
  import("@/features/users/UsersView").then((mod) => ({
    default: mod.UsersView,
  })),
);
const LogsView = lazyWithReload(() =>
  import("@/features/logs/LogsView").then((mod) => ({
    default: mod.LogsView,
  })),
);
const AccountView = lazyWithReload(() =>
  import("@/features/account/AccountView").then((mod) => ({
    default: mod.AccountView,
  })),
);
const ApiKeysView = lazyWithReload(() =>
  import("@/features/api-keys/ApiKeysView").then((mod) => ({
    default: mod.ApiKeysView,
  })),
);
const DataSourcesView = lazyWithReload(() =>
  import("@/features/data-sources/DataSourcesView").then((mod) => ({
    default: mod.DataSourcesView,
  })),
);
const WebhooksView = lazyWithReload(() =>
  import("@/features/webhooks/WebhooksView").then((mod) => ({
    default: mod.WebhooksView,
  })),
);
const StatusPageView = lazyWithReload(() =>
  import("@/features/status/StatusPageView").then((mod) => ({
    default: mod.StatusPageView,
  })),
);
const TimezoneView = lazyWithReload(() =>
  import("@/features/timezone/TimezoneView").then((mod) => ({
    default: mod.TimezoneView,
  })),
);
const PreExistingDataView = lazyWithReload(() =>
  import("@/features/extraction/PreExistingDataView").then((mod) => ({
    default: mod.PreExistingDataView,
  })),
);
const RetriesView = lazyWithReload(() =>
  import("@/features/extraction/RetriesView").then((mod) => ({
    default: mod.RetriesView,
  })),
);
const GranularView = lazyWithReload(() =>
  import("@/features/delivery/GranularView").then((mod) => ({
    default: mod.GranularView,
  })),
);
const NonStructuredView = lazyWithReload(() =>
  import("@/features/delivery/NonStructuredView").then((mod) => ({
    default: mod.NonStructuredView,
  })),
);
const SdkKeysView = lazyWithReload(() =>
  import("@/features/sdk-keys/SdkKeysView").then((mod) => ({
    default: mod.SdkKeysView,
  })),
);
const ConnectionPageView = lazyWithReload(() =>
  import("@/features/connection-page/ConnectionPageView").then((mod) => ({
    default: mod.ConnectionPageView,
  })),
);
const SimulatorView = lazyWithReload(() =>
  import("@/features/simulator/SimulatorView").then((mod) => ({
    default: mod.SimulatorView,
  })),
);
const DataStructureView = lazyWithReload(() =>
  import("@/features/data-structure/DataStructureView").then((mod) => ({
    default: mod.DataStructureView,
  })),
);
const ScoreView = lazyWithReload(() =>
  import("@/features/score/ScoreView").then((mod) => ({
    default: mod.ScoreView,
  })),
);
// Showcase-only views (gated nav, WP7). The routes exist so the gated
// nav items resolve; the default portal never links to them.
const BillingView = lazyWithReload(() =>
  import("@/features/billing").then((mod) => ({
    default: mod.BillingView,
  })),
);

/**
 * Landing-origin handoff component. Anything OUTSIDE /home (the
 * landing-owned routes like /legal, or stale bookmarks at /) bounces
 * cross-origin to the landing app behind the branded overlay so the
 * page change is invisible (CPF-NFR-028).
 *
 * `LANDING_ORIGIN` is imported from `@rook/shared/lib/portalOrigins` (the single
 * source of truth) — it's `clients.portal.rook-connect.com` now and flips to
 * `clients.portal.tryrook.io` at cutover via the one `CUTOVER_IN_PROGRESS` flag,
 * so this file never hardcodes a host.
 */
const HandoffToLanding = () => (
  <CrossOriginHandoff
    targetOrigin={LANDING_ORIGIN}
    targetEnvLabel="Landing"
  />
);

export const App = () => (
  <AuthProvider>
    <Routes>
      <Route
        path="/home"
        element={
          <AuthGuard>
            <HomeRoute />
          </AuthGuard>
        }
      >
        <Route index element={<DashboardView />} />
        <Route path="users" element={<UsersView />} />
        <Route path="score" element={<ScoreView />} />
        <Route path="logs" element={<LogsView />} />
        <Route path="data-sources" element={<DataSourcesView />} />

        <Route
          path="extraction"
          element={<Navigate to="/home/extraction/timezone" replace />}
        />
        <Route path="extraction/timezone" element={<TimezoneView />} />
        <Route
          path="extraction/pre-existing"
          element={<PreExistingDataView />}
        />
        <Route path="extraction/retries" element={<RetriesView />} />

        <Route
          path="delivery"
          element={<Navigate to="/home/delivery/webhooks" replace />}
        />
        <Route path="delivery/webhooks" element={<WebhooksView />} />
        <Route path="delivery/granular" element={<GranularView />} />
        <Route path="delivery/non-structured" element={<NonStructuredView />} />
        <Route path="delivery/data-structure" element={<DataStructureView />} />

        <Route
          path="develop"
          element={<Navigate to="/home/develop/simulator" replace />}
        />
        <Route path="develop/simulator" element={<SimulatorView />} />
        <Route path="develop/connection-page" element={<ConnectionPageView />} />

        <Route
          path="auth"
          element={<Navigate to="/home/auth/api-keys" replace />}
        />
        <Route path="auth/api-keys" element={<ApiKeysView />} />
        <Route path="auth/sdk" element={<SdkKeysView />} />

        <Route path="account" element={<AccountView />} />
        <Route path="status-page" element={<StatusPageView />} />

        {/* Showcase-only (gated nav, WP7) — mock surfaces for the
            design-decision meeting; never linked on the default portal. */}
        <Route path="billing" element={<BillingView />} />

        {/* Catch-all inside /home → bounce to dashboard, never 404. */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>

      {/* Anything outside /home is landing-owned — bounce to the
          landing's matching path behind the branded overlay so the
          customer never sees a blank tab (CPF-NFR-028). Catches
          `/legal/*` deep-links, `/` post-bookmark, `/create-account`
          stale links, etc. */}
      <Route path="*" element={<HandoffToLanding />} />
    </Routes>
  </AuthProvider>
);
